<template>
  <div class="acted-alarms-vs-recognized-alarms">
    <dashboard-items-header
      class="mb-3"
      :title="$t('dashboard.acted-alarms-vs-recognized-alarms')"
      :periodDays="periodDays"
      @onClickPeriodFilter="onClickPeriodFilterHandler"
    />
    <s-loader
      v-if="loading"
      class="acted-alarms-vs-recognized-alarms__loading p-5"
      size="md"
    />
    <e-chart
      v-else
      class="acted-alarms-vs-recognized-alarms__chart"
      ref="echarts"
      autoresize
      :options="options"
      @click="onSelected"
    />
  </div>
</template>

<script>
import { AlarmHistoryStatusFilter, AlarmType } from 'sigma-mappings';
import DashboardItemsHeader from '@/components/header/DashboardItemsHeader';
import ServiceDashboard from '@/domain/services/generalPanel/dashboard';
import periods from '@/domain/enums/periods-dropdown-filter';
import themeService from '@/services/themeService';
import AlarmsReportMixin from '@/domain/mixins/generalPanel/reports/alarmsReportMixin';
import {
  formatter as dateTimeFormatter,
  getInitialDate,
  getNow,
  getStartOfDay,
  minus,
  setToEndOfDay,
} from '@/services/dateTime/datetimeService';

export default {
  name: 'ActedAlarmsVsRecognizedAlarmsChart',
  props: {
    filters: {
      type: Object,
      required: true,
    },
  },
  components: {
    DashboardItemsHeader,
  },
  mixins: [AlarmsReportMixin],
  data: () => ({
    periodDays: periods.SEVEN,
    chartData: null,
    loading: true,
    recognizedColor: '',
    actedColor: '',
  }),
  created() {
    this.actedColor = themeService.getStyleVar('color-acted-and-not-recognized');
    this.recognizedColor = themeService.getStyleVar('color-acted-and-recognized');
  },
  computed: {
    getFilter() {
      return {
        ...this.filters,
        periodDays: this.periodDays,
      };
    },
    options() {
      const series = this.getChartSeries();
      return {
        tooltip: {
          axisPointer: {
            label: {
              formatter: data => this.formatDate(data?.value),
            },
          },
          trigger: 'axis',
          backgroundColor: themeService.getStyleVar('color-background-tooltip'),
          textStyle: {
            color: 'white',
          },
        },
        grid: {
          left: '10%',
          right: '10%',
          bottom: '20',
          top: '60',
        },
        legend: {
          itemHeight: 8,
          itemWidth: 8,
          itemGap: 40,
          icon: 'circle',
          left: true,
          textStyle: {
            color: [],
            fontSize: '0.75rem',
            fontWeight: 'bold',
          },
        },
        axisPointer: {
          show: true,
          triggerTooltip: false,
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: [
            '',
            ...this.getDatesFromChartData(),
          ],
          axisLine: {
            lineStyle: {
              color: themeService.getStyleVar('color-dash-border'),
              width: 2,
            },
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            show: this.chartData.length > 0,
            lineStyle: {
              type: 'dashed',
              color: themeService.getStyleVar('color-grid-x-axis'),
            },
          },
          axisLabel: {
            formatter: value => this.formatDate(value),
            textStyle: {
              fontWeight: 'bold',
            },
          },
        },
        yAxis: {
          type: 'value',
          axisLine: {
            show: true,
            lineStyle: {
              color: themeService.getStyleVar('color-dash-border'),
              width: 2,
            },
          },
          splitLine: {
            lineStyle: {
              type: 'dashed',
              color: themeService.getStyleVar('color-grid-y-axis'),
            },
          },
          axisLabel: {
            textStyle: {
              fontWeight: 'bold',
            },
          },
        },
        series,
      };
    },
  },
  methods: {
    formatDate(value) {
      if (value) {
        const format = this.$t('date-time.label.day-month-mask');
        return dateTimeFormatter(value, format, 'Etc/UTC');
      }

      return '';
    },
    loadChartData() {
      this.loading = true;
      ServiceDashboard
        .getActedAlarmsVsRecognizedAlarms(this.getFilter)
        .then((response) => {
          this.chartData = response;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getChartSeries() {
      const commonSeriesData = {
        type: 'line',
        symbol: 'circle',
        symbolSize: 12,
        lineStyle: {
          width: 2.5,
        },
      };

      const actedAlarmsValues = this.getValuesFromChartData(AlarmType.ACTED_ALARMS);
      const recognizedAlarmsValues = this.getValuesFromChartData(AlarmType.RECOGNIZED_ALARMS);

      const actedAlarms = {
        ...commonSeriesData,
        name: this.$t('dashboard.acteds').toUpperCase(),
        color: this.actedColor,
        data: actedAlarmsValues,
      };

      const recognizedAlarms = {
        ...commonSeriesData,
        name: this.$t('dashboard.recognized-alarms').toUpperCase(),
        color: this.recognizedColor,
        data: recognizedAlarmsValues,
      };

      return [actedAlarms, recognizedAlarms];
    },
    getDatesFromChartData() {
      if (!this.chartData) {
        return [];
      }

      const [firstValue] = this.chartData;
      return (firstValue?.values || []).map(x => x.date);
    },
    getValuesFromChartData(name) {
      const chart = this.chartData?.find(x => x.name === name);
      if (!chart || !chart?.values) {
        return [];
      }

      const initialSerieValue = 0;

      return [
        initialSerieValue,
        ...(chart.values || []).map(x => x.value),
      ];
    },
    onClickPeriodFilterHandler(newPeriod) {
      this.periodDays = newPeriod;
      this.loadChartData();
    },
    getStartingDate() {
      if (this.periodDays === periods.ALL) {
        return getInitialDate();
      }
      return minus(getStartOfDay({ timeZone: 'Etc/UTC' }), { days: this.periodDays - 1 });
    },
    onSelected(params) {
      const startDate = this.getStartingDate();
      const endDate = setToEndOfDay(params.name || getNow(), { timeZone: 'Etc/UTC' });

      const filter = {
        initialDateTime: startDate,
        finalDateTime: endDate,
        status: this.getStatusByColor(params?.color),
        companiesIds: this.filters.companiesIds,
        installationsIds: this.filters.installationsIds,
        regionalIds: this.filters.regionalsIds,
      };
      this.openAlarmReport(filter);
    },
    getStatusByColor(color) {
      switch (color) {
        case this.actedColor:
          return [
            AlarmHistoryStatusFilter.ACTED_NOT_RECOGNIZED,
            AlarmHistoryStatusFilter.ACTED_RECOGNIZED,
          ];
        case this.recognizedColor:
          return [
            AlarmHistoryStatusFilter.ACTED_RECOGNIZED,
            AlarmHistoryStatusFilter.NOT_ACTED_RECOGNIZED,
          ];
        default: return [];
      }
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"
.acted-alarms-vs-recognized-alarms
  width: 100%
  height: 100%
  padding-bottom: 1rem
  +media-max-md()
    height: 28.5rem

  &__loading
    min-height: 200px

  &__chart
    height: calc(100% - 3em)
    width: 100%
</style>

